import type { AssignmentTemplate } from '../../model/assignments/interfaces'
import type { ApiResponse } from '@shared/api'

import { useCommonStore } from '@shared/model'
import { getAxiosErrorMessage } from '@shared/api'
import { useAdminRoutes } from '@shared/lib/routes'
import { useToast } from '@shared/ui/toast'

interface AssignmentTemplateShowApiOptions {
  templateId: string | number
  onStart?: () => void
  onSuccess?: (template: AssignmentTemplate) => void
  onError?: (message: string) => void
  onFinish?: () => void
}

/** `GET /admin-api/assignment-templates/{template_id}`. No response normalizer: unlike the
 *  assessment document, this one carries no maps that PHP could round-trip as `[]`. */
export const useAssignmentTemplateShowApi = async (
  options: AssignmentTemplateShowApiOptions,
): Promise<AssignmentTemplate | null> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()
  const commonStore = useCommonStore()

  const url = useAdminRoutes('admin.assignment-templates.show', { template_id: options.templateId })

  options.onStart?.()
  commonStore.setLoading(true)

  try {
    if (!url) {
      const msg = 'Unexpected error. Invalid request URL.'
      toast.error(msg)
      options.onError?.(msg)
      return null
    }

    const { data } = await $axios.get<ApiResponse>(url)

    if (!data.success) {
      toast.error(data.message)
      options.onError?.(data.message)
      return null
    }

    const template: AssignmentTemplate | null = data.data?.template ?? null

    if (!template) {
      const msg = 'Unexpected error. The assignment template is missing from the response.'
      toast.error(msg)
      options.onError?.(msg)
      return null
    }

    options.onSuccess?.(template)
    return template
  } catch (err: unknown) {
    const msg = getAxiosErrorMessage(err)
    toast.error(msg)
    options.onError?.(msg)
    return null
  } finally {
    commonStore.setLoading(false)
    options.onFinish?.()
  }
}
